'use client';

import { useQuery } from '@tanstack/react-query';

/**
 * Hook to fetch active live streams
 * @param {Object} options - Query options
 * @returns {Object} Query result with live stream data
 */
const useGetLiveStreams = (options = {}) => {
  return useQuery({
    queryKey: ['liveStreams'],
    queryFn: async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        const response = await fetch('/discover/api/live-streams', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          if (response.status === 404) {
            return { calls: [] };
          }
          throw new Error(`Failed to fetch live streams: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          return { calls: [] };
        }

        const text = await response.text();
        if (!text || text.trim() === '') {
          return { calls: [] };
        }

        try {
          return JSON.parse(text);
        } catch {
          return { calls: [] };
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return { calls: [] };
        }
        return { calls: [] };
      }
    },
    refetchInterval: (query) => {
      // Poll more frequently when a stream is active, less frequently when idle
      const hasActiveStream = query.state.data?.calls?.length > 0;
      return hasActiveStream ? 120000 : 300000; // 2min when live, 5min when idle
    },
    refetchOnWindowFocus: true,
    staleTime: 60000, // Cache for 1 minute to reduce redundant fetches
    ...options,
  });
};

export default useGetLiveStreams;
